import { placeCollectables, type Collectable } from "./collectables";
import { playable, surfaceAt, type Level } from "./level";
import { Run, SCORE_GOAL_NAMES, type Goal, type MapGoals } from "./run";
import type { SkaterEvent } from "./skater";
import type { Tag } from "./graffiti";
import { targetRange } from "./targets";
import { TASK_KINDS, type Riding } from "./tasks";

// Challenges in free skate (THPS 4 / Underground style): each of the map's
// goals has a glowing marker in the park. Skate into one, press △ and that
// goal runs alone on its own short clock. Same goal list and ids as the
// 2-minute run (`run.ts`), so a goal done in one shows as done in the other.
// Sim-free: Game feeds it the skater's position and events.

/** "trick", "combo", "targets" and "graffiti" are tasks (#66, tasks.ts): a named trick or a special; a big or long combo; paintball targets (#137); graffiti to wash off (#151). */
export type ChallengeKind = "score" | "gap" | "collect" | "trick" | "combo" | "targets" | "graffiti";

/** Seconds on a challenge's clock. */
export const CHALLENGE_TIME = { score: [45, 60, 60], gap: 30, collect: 60, trick: 30, combo: 45, targets: 30, graffiti: 30 } as const;

/** The graffiti challenge's marker goes about this far out from the first tag (m, #151): just behind where you wash it from. */
const TAG_MARKER_OUT = 3.5;
/** ...on the floor this far out from it, about where you stand to wash it (m). */
const TAG_WASH_FROM = 2;

/** How close (horizontally) the skater has to be to a marker to start it. */
export const MARKER_R = 1.6;
/** Flat, rail-free ground a marker needs around it. */
export const MARKER_CLEAR = 1.8;
/** Closest two markers (or a marker and the spawn) may be. */
export const MARKER_SPACING = 4.5;

/** Where the graffiti challenge's marker goes: out from the first tag, on the floor you wash it from (a deck, #154), facing the tag. */
function graffitiAt(level: Level, tag: Tag): { near: { x: number; z: number }; face: Tag; floor: number } {
  const near = { x: tag.x + tag.nx * TAG_MARKER_OUT, z: tag.z + tag.nz * TAG_MARKER_OUT };
  return { near, face: tag, floor: surfaceAt(level, tag.x + tag.nx * TAG_WASH_FROM, tag.z + tag.nz * TAG_WASH_FROM).h };
}

/** A challenge's marker in the park, and what it starts. */
export interface ChallengeSpot {
  /** The goal id, as in `Run.goals` ("score-0", "gap-Rail Hop", "skate"...). */
  id: string;
  kind: ChallengeKind;
  /** The goal: "High Score: 15,000", "Land the Rail Hop gap". */
  text: string;
  /** Seconds on the clock. */
  time: number;
  x: number;
  z: number;
  /** Which way a retry faces the skater: toward what the challenge is about. */
  yaw: number;
  /** The deck it stands on, when that's not the floor (a targets range up on one, #137). */
  y?: number;
}

/**
 * The map's challenges and where their markers go, in goal-list order. Each
 * marker goes on the flat ground nearest what it's about (score goals by the
 * spawn, a gap by its takeoff, letters by the first letter), clear of ramps,
 * rails and other markers. A goal with nowhere to put its marker is left out
 * (`maps.test.ts`-style checks make sure the shipped maps place them all).
 */
export function challengeSpots(goals: MapGoals, level: Level): ChallengeSpot[] {
  const pickups = placeCollectables(goals, level);
  const first = (kind: Collectable["kind"]) => pickups.find((c) => c.kind === kind);
  const want: {
    id: string;
    kind: ChallengeKind;
    text: string;
    time: number;
    near: { x: number; z: number };
    face: { x: number; z: number };
    /** The ground height its marker needs (0, the floor, unless given). */
    floor?: number;
  }[] = [];
  const spawn = level.spawn;
  const center = { x: 0, z: 0 };
  goals.scores.forEach((points, i) =>
    want.push({
      id: `score-${i}`,
      kind: "score",
      text: `${SCORE_GOAL_NAMES[i]}: ${points.toLocaleString("en-US")}`,
      time: CHALLENGE_TIME.score[i] ?? CHALLENGE_TIME.score[2],
      near: spawn,
      face: center,
    }),
  );
  const skate = first("skate");
  if (skate) want.push({ id: "skate", kind: "collect", text: "Collect S-K-A-T-E", time: CHALLENGE_TIME.collect, near: skate, face: skate });
  const combo = first("combo");
  if (combo)
    want.push({ id: "combo", kind: "collect", text: "Collect C-O-M-B-O in one combo", time: CHALLENGE_TIME.collect, near: combo, face: combo });
  for (const name of goals.gaps) {
    const gap = level.gaps?.find((g) => g.name === name);
    if (!gap) continue;
    want.push({ id: `gap-${name}`, kind: "gap", text: `Land the ${name} gap`, time: CHALLENGE_TIME.gap, near: gap.from, face: gap.to });
  }
  // The tape's marker doesn't give its hiding place away: it waits in the middle of the park.
  if (first("tape")) want.push({ id: "tape", kind: "collect", text: "Find the Secret Tape", time: CHALLENGE_TIME.collect, near: center, face: spawn });
  // Tasks: by their gap's takeoff if they name one, at the range facing the
  // row for the targets (#137), in front of the first tag facing it for the
  // graffiti (#151), otherwise by the spawn like the score goals.
  const range = level.targets ? targetRange(level.targets) : null;
  const tag = level.graffiti?.[0];
  for (const t of goals.tasks ?? []) {
    const def = TASK_KINDS[t.kind];
    const gap = t.gap === undefined ? undefined : level.gaps?.find((g) => g.name === t.gap);
    const time = CHALLENGE_TIME[def.marker];
    const at =
      def.marker === "targets" && range
        ? { near: range, face: range.row, floor: range.floor }
        : def.marker === "graffiti" && tag
          ? graffitiAt(level, tag)
          : { near: gap?.from ?? spawn, face: gap?.to ?? center };
    want.push({ id: t.id, kind: def.marker, text: def.text(t), time, ...at });
  }

  const taken: { x: number; z: number }[] = [spawn];
  const out: ChallengeSpot[] = [];
  for (const w of want) {
    const floor = w.floor ?? 0;
    const at = findMarkerSpot(level, w.near, taken, floor);
    if (!at) continue;
    taken.push(at);
    const dx = w.face.x - at.x;
    const dz = w.face.z - at.z;
    const yaw = Math.hypot(dx, dz) > 0.5 ? Math.atan2(dx, dz) : spawn.yaw;
    out.push({ id: w.id, kind: w.kind, text: w.text, time: w.time, x: at.x, z: at.z, yaw, ...(floor ? { y: floor } : {}) });
  }
  return out;
}

/** The nearest spot to `near` where a marker fits, searching outward in rings; null if none does. */
function findMarkerSpot(
  level: Level,
  near: { x: number; z: number },
  taken: readonly { x: number; z: number }[],
  floor = 0,
): { x: number; z: number } | null {
  const toCenter = Math.atan2(-near.x, -near.z);
  for (let r = 0; r <= level.bounds * 2; r += 1.5) {
    const n = r === 0 ? 1 : Math.max(6, Math.round((2 * Math.PI * r) / 1.5));
    for (let k = 0; k < n; k++) {
      // Alternate either side of "toward the middle", so the nearest fit wins and leans inward.
      const step = Math.ceil(k / 2) * (k % 2 ? 1 : -1);
      const a = toCenter + (step * 2 * Math.PI) / n;
      const x = Math.round((near.x + Math.sin(a) * r) * 10) / 10;
      const z = Math.round((near.z + Math.cos(a) * r) * 10) / 10;
      if (markerFits(level, x, z, taken, MARKER_CLEAR, floor)) return { x, z };
    }
  }
  return null;
}

/**
 * Whether a marker at (x, z) sits on flat bare ground (`clear` metres of it
 * around) at height `floor` (the park's floor unless given), inside the fence
 * and off any sand, clear of rails and other markers.
 */
export function markerFits(
  level: Level,
  x: number,
  z: number,
  taken: readonly { x: number; z: number }[] = [],
  clear = MARKER_CLEAR,
  floor = 0,
): boolean {
  if (!playable(level, x, z, clear + 0.5)) return false;
  if (taken.some((t) => Math.hypot(t.x - x, t.z - z) < MARKER_SPACING)) return false;
  for (let i = 0; i <= 12; i++) {
    const r = i === 0 ? 0 : i <= 6 ? clear / 2 : clear;
    const a = (i * Math.PI) / 3;
    const s = surfaceAt(level, x + Math.sin(a) * r, z + Math.cos(a) * r);
    if (s.h !== floor || s.gx !== 0 || s.gz !== 0) return false;
  }
  return level.rails.every((rail) => segmentDistance(x, z, rail.a, rail.b) > clear);
}

function segmentDistance(x: number, z: number, a: { x: number; z: number }, b: { x: number; z: number }): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / len2));
  return Math.hypot(x - (a.x + dx * t), z - (a.z + dz * t));
}

/** The marker the skater at (x, z) is standing in, if any. */
export function spotAt(spots: readonly ChallengeSpot[], x: number, z: number): ChallengeSpot | null {
  let best: ChallengeSpot | null = null;
  let bestD = MARKER_R;
  for (const s of spots) {
    const d = Math.hypot(s.x - x, s.z - z);
    if (d <= bestD) {
      best = s;
      bestD = d;
    }
  }
  return best;
}

/**
 * `ready` while the start card shows (the clock waits); `running`, then
 * `overtime` while a combo in progress at the buzzer finishes; `passed` as
 * soon as the goal is done, `failed` if the clock runs out first.
 */
export type ChallengePhase = "ready" | "running" | "overtime" | "passed" | "failed";

/** One goal on its own clock. Retrying is a new `Challenge`. */
export class Challenge {
  phase: ChallengePhase = "ready";
  /** The goal alone, as a short run: it does the clock, the pickups and the goal logic. */
  readonly run: Run;
  private startScore = 0;

  /** `pickups` are the map's collectables (`placeCollectables`); only the ones this goal needs come out. */
  constructor(
    readonly spot: ChallengeSpot,
    goals: MapGoals,
    pickups: readonly Collectable[] = [],
  ) {
    this.run = new Run(goals, pickups, { time: spot.time, only: spot.id });
  }

  get goal(): Goal {
    return this.run.goals[0]!;
  }

  get timeLeft(): number {
    return this.run.timeLeft;
  }

  /** On the clock or finishing the last combo: the skater's what counts. */
  get live(): boolean {
    return this.phase === "running" || this.phase === "overtime";
  }

  get over(): boolean {
    return this.phase === "passed" || this.phase === "failed";
  }

  /** Starts the clock. Score challenges count points from here: `score` is the skater's score now. */
  begin(score: number): void {
    if (this.phase !== "ready") return;
    this.startScore = score;
    this.phase = "running";
  }

  /** Picks up what the skater is touching (see `Run.touch`). */
  touch(x: number, y: number, z: number, busy: boolean): Collectable[] {
    return this.live ? this.run.touch(x, y, z, busy) : [];
  }

  /**
   * Advances by `dt` of sim time; `score` is the skater's total score, `busy`
   * and `riding` as for `Run.step`. Returns true on the step the challenge ends.
   */
  step(dt: number, score: number, busy: boolean, events: readonly SkaterEvent[], riding?: Riding): boolean {
    if (!this.live) return false;
    this.run.step(dt, Math.max(0, score - this.startScore), busy, events, riding);
    if (this.goal.done) this.phase = "passed";
    else if (this.run.phase === "done") this.phase = "failed";
    else this.phase = this.run.phase;
    return this.over;
  }
}

/** A free-skate challenge (#47) in progress, for its start card, timer and pass/fail card. */
export interface ChallengeHud {
  id: string;
  kind: ChallengeKind;
  /** The goal: "High Score: 15,000". */
  text: string;
  /** Seconds on its clock at the start. */
  time: number;
  /** Whole seconds left, rounded up. */
  seconds: number;
  phase: ChallengePhase;
  /** Points scored since it began (score challenges). */
  score: number;
  /** Letters grabbed so far, like "S K · · E" (collect challenges). */
  progress: string | null;
}

/** The challenge marker (or hub, #86) the skater is standing in: △ starts it (opens the hub's list), unless it's locked. */
export interface ChallengePrompt {
  text: string;
  done: boolean;
  /** A hub: △ opens its list of challenges. */
  hub: boolean;
  /** What it waits on, like "High Score: 15,000 and Collect S-K-A-T-E" (#66); null when it's open. */
  locked: string | null;
}

/** A challenge's HUD state; `prev` back when nothing it shows has changed, so the HUD diff sees no change. */
export function challengeHud(ch: Challenge | null, prev: ChallengeHud | null): ChallengeHud | null {
  if (!ch) return null;
  const s = ch.spot;
  const seconds = Math.ceil(ch.timeLeft);
  const progress = s.id === "skate" || s.id === "combo" ? ch.run.letterProgress(s.id) : null;
  if (prev && prev.id === s.id && prev.seconds === seconds && prev.phase === ch.phase && prev.score === ch.run.score && prev.progress === progress)
    return prev;
  return { id: s.id, kind: s.kind, text: s.text, time: s.time, seconds, phase: ch.phase, score: ch.run.score, progress };
}

/** The prompt for the marker the skater's in (null for none); `prev` back when it hasn't changed. */
export function challengePrompt(
  spot: { id: string; text: string } | null,
  done: (id: string) => boolean,
  prev: ChallengePrompt | null,
  needs: (id: string) => string | null = () => null,
  hub = false,
): ChallengePrompt | null {
  if (!spot) return null;
  const isDone = done(spot.id);
  const locked = needs(spot.id);
  if (prev?.text === spot.text && prev.done === isDone && prev.locked === locked && prev.hub === hub) return prev;
  return { text: spot.text, done: isDone, hub, locked };
}

/** Goals done this session (in a run or a challenge), per map. Session-only until saving exists. */
export class GoalsDone {
  private byMap = new Map<string, Set<string>>();

  /** Marks a goal done; true the first time (which pays its rung's stat points, #66). */
  add(mapId: string, goalId: string): boolean {
    const done = this.byMap.get(mapId) ?? new Set<string>();
    if (done.has(goalId)) return false;
    this.byMap.set(mapId, done.add(goalId));
    return true;
  }

  has(mapId: string, goalId: string): boolean {
    return this.byMap.get(mapId)?.has(goalId) ?? false;
  }
}

/** The start card's line on how to do it. */
export function challengeHint(id: string, kind: ChallengeKind): string {
  if (kind === "score") return "Score it before time's up. Points count from when you go, and a combo still going at the buzzer counts.";
  if (kind === "gap") return "Land it before time's up. It's in the Gap list if you need a hint where.";
  if (kind === "trick") return "Land it before time's up: it counts once the combo it's in lands.";
  if (kind === "combo") return "Land one combo that does it before time's up. Manual between tricks to keep it going.";
  if (kind === "graffiti")
    return "Step off the board, get the power washer out (R1 round the pack) and hold □ on each tag until it's gone, before time's up.";
  if (kind === "targets") return "Step off the board, get the paintball gun out and burst them all before time's up. Close is good enough: it aims for you.";
  if (id === "skate") return "Grab all five letters. They float around the park.";
  if (id === "combo") return "Grab all five letters in one combo: manual between them to keep it going.";
  return "It's somewhere up high. Find it and grab it.";
}
